"use client";

import { AnimatePresence, motion, useInView } from "framer-motion";
import React, {
    useEffect,
    useRef,
    useState,
    useCallback,
    type CSSProperties,
} from "react";

type ImageValue = string | { src?: string; srcSet?: string; alt?: string };
export interface CarouselItem {
    buttonImage?: ImageValue;
    image?: ImageValue;
    label?: string;
    role?: string;
    bio?: string;
}
interface FontValue {
    fontFamily?: string;
    fontWeight?: number | string;
    fontSize?: number | string;
    fontStyle?: string;
    letterSpacing?: number | string;
    lineHeight?: number | string;
}
const srcOf = (v?: ImageValue): string =>
    typeof v === "string" ? v : v?.src || "";

interface KlarnaCarouselProps {
    items?: CarouselItem[];
    cardRadius?: number;
    imageWidth?: number;
    imageHeight?: number;
    buttonCount?: number;
    buttonSize?: number;
    buttonRadius?: number;
    curve?: number;
    gap?: number;
    labelShow?: boolean;
    labelX?: number;
    labelY?: number;
    labelColor?: string;
    labelFont?: FontValue;
    backgroundColor?: string;
    style?: CSSProperties;
}

const DEFAULT_ITEMS: CarouselItem[] = [
    {
        image: "https://imagedelivery.net/IEUjvl3YUlxY-MrTpOAWDQ/8fd4d2a3-a363-4658-d6ee-84790bc8f300/w=800",
        buttonImage: "https://imagedelivery.net/IEUjvl3YUlxY-MrTpOAWDQ/8fd4d2a3-a363-4658-d6ee-84790bc8f300/w=800",
        label: "Sophia Benett",
        role: "Rescue Operations",
        bio: "Dedicated specialist overseeing rapid dispatching and logistics."
    },
    {
        image: "https://imagedelivery.net/IEUjvl3YUlxY-MrTpOAWDQ/4d1fe81d-5289-4e08-b381-03e4e9efed00/w=800",
        buttonImage: "https://imagedelivery.net/IEUjvl3YUlxY-MrTpOAWDQ/4d1fe81d-5289-4e08-b381-03e4e9efed00/w=800",
        label: "Isabella Foster",
        role: "Lead Coordinator",
        bio: "Managing emergency response pipelines with a focus on real-time routing."
    },
    {
        image: "https://imagedelivery.net/IEUjvl3YUlxY-MrTpOAWDQ/6ab26fe4-5016-4c65-01e8-b3a71ea08200/w=800",
        buttonImage: "https://imagedelivery.net/IEUjvl3YUlxY-MrTpOAWDQ/6ab26fe4-5016-4c65-01e8-b3a71ea08200/w=800",
        label: "Grace Turner",
        role: "Fleet Supervisor",
        bio: "Ensuring all recovery vehicles meet strict safety and tech compliance."
    }
];

const CARD_THEMES = [
    {
        bg: "linear-gradient(135deg, #1d170f 0%, #0d0c0a 100%)", // Amber/Gold accent
        border: "1px solid rgba(255, 174, 0, 0.35)",
        shadow: "0 15px 45px rgba(0, 0, 0, 0.65), 0 0 25px rgba(255, 174, 0, 0.12)",
    },
    {
        bg: "linear-gradient(135deg, #0e171b 0%, #080c0d 100%)", // Blue accent
        border: "1px solid rgba(0, 168, 255, 0.35)",
        shadow: "0 15px 45px rgba(0, 0, 0, 0.65), 0 0 25px rgba(0, 168, 255, 0.12)",
    },
    {
        bg: "linear-gradient(135deg, #150f1b 0%, #0b080d 100%)", // Purple/Violet accent
        border: "1px solid rgba(186, 85, 211, 0.35)",
        shadow: "0 15px 45px rgba(0, 0, 0, 0.65), 0 0 25px rgba(186, 85, 211, 0.12)",
    },
    {
        bg: "linear-gradient(135deg, #0e1b15 0%, #080d0a 100%)", // Emerald/Green accent
        border: "1px solid rgba(0, 204, 126, 0.35)",
        shadow: "0 15px 45px rgba(0, 0, 0, 0.65), 0 0 25px rgba(0, 204, 126, 0.12)",
    },
    {
        bg: "linear-gradient(135deg, #1b0f0f 0%, #0d0808 100%)", // Ruby/Red accent
        border: "1px solid rgba(255, 87, 87, 0.35)",
        shadow: "0 15px 45px rgba(0, 0, 0, 0.65), 0 0 25px rgba(255, 87, 87, 0.12)",
    },
    {
        bg: "linear-gradient(135deg, #1b170f 0%, #0d0c08 100%)", // Bronze/Orange accent
        border: "1px solid rgba(255, 145, 0, 0.35)",
        shadow: "0 15px 45px rgba(0, 0, 0, 0.65), 0 0 25px rgba(255, 145, 0, 0.12)",
    },
];

function modIdx(i: number, n: number) {
    return ((i % n) + n) % n;
}

function easeCubicInOut(p: number) {
    return p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
}

export default function KlarnaCarousel(props: KlarnaCarouselProps) {
    const {
        items = DEFAULT_ITEMS,
        cardRadius = 12,
        imageWidth = 268,
        imageHeight = 331,
        buttonCount = 5,
        buttonSize = 70,
        buttonRadius = 35, // Circle buttons
        curve = 4,
        gap = 55,
        labelShow = true,
        labelX = -13,
        labelY = -6,
        labelColor = "#ffffff",
        labelFont = {
            fontFamily: "Poppins, sans-serif",
            fontWeight: 400,
            fontSize: "1.1rem",
            lineHeight: "1.3em",
            letterSpacing: "0em",
        },
        backgroundColor = "rgba(255, 255, 255, 0)",
    } = props;

    const list = items?.length ? items : DEFAULT_ITEMS;
    const M = list.length;

    const posRef = useRef(0);
    const [posDisplay, setPosDisplay] = useState(0);
    const rafRef = useRef<number | null>(null);
    const animRef = useRef({ startPos: 0, targetPos: 0, startTime: 0 });
    const [dir, setDir] = useState(1);

    const active = modIdx(Math.round(posDisplay), M);
    const currentTheme = CARD_THEMES[active % CARD_THEMES.length];

    const half = Math.floor(Math.min(Math.max(1, buttonCount), M) / 2);
    const buffer = half + 1;

    const cardRadiusPx = cardRadius;
    const buttonRadiusPx = buttonRadius;
    const t = Math.max(0.0001, Math.min(10, curve) / 10);
    const step = buttonSize + gap;
    const dPsi = ((Math.PI * 2) / M) * t;
    const R = step / (2 * Math.sin(dPsi / 2));
    const baseTop = buttonSize * 0.9;
    const fadeInner = Math.max(0, half - 0.4);
    const fadeEnd = half + 0.6;
    const maxPsi = Math.min(Math.PI, fadeEnd * dPsi);
    const stripHeight =
        baseTop + R * (1 - Math.cos(maxPsi)) + buttonSize / 2 + 16;

    const select = useCallback(
        (itemIdx: number) => {
            const currentActive = modIdx(Math.round(posRef.current), M);
            if (itemIdx === currentActive) return;

            let delta = itemIdx - Math.round(posRef.current);
            delta = ((delta % M) + M) % M;
            if (delta > M / 2) delta -= M;
            setDir(Math.sign(delta));

            if (rafRef.current) cancelAnimationFrame(rafRef.current);

            animRef.current = {
                startPos: posRef.current,
                targetPos: posRef.current + delta,
                startTime: performance.now(),
            };

            const DURATION = 320;
            function tick(now: number) {
                const { startPos, targetPos, startTime } = animRef.current;
                const progress = Math.min(1, (now - startTime) / DURATION);
                posRef.current =
                    startPos + (targetPos - startPos) * easeCubicInOut(progress);
                setPosDisplay(posRef.current);
                if (progress < 1) {
                    rafRef.current = requestAnimationFrame(tick);
                } else {
                    posRef.current = targetPos;
                    setPosDisplay(targetPos);
                    rafRef.current = null;
                }
            }
            rafRef.current = requestAnimationFrame(tick);
        },
        [M]
    );

    const [isHovered, setIsHovered] = useState(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const isInView = useInView(containerRef, { amount: 0.15 });

    const [isTabVisible, setIsTabVisible] = useState(true);

    useEffect(() => {
        const handleVisibilityChange = () => {
            setIsTabVisible(document.visibilityState === "visible");
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, []);

    useEffect(() => {
        if (M <= 1 || isHovered || !isInView || !isTabVisible) return;

        const interval = setInterval(() => {
            if (rafRef.current !== null) return; // Guard: skip if already mid-animation
            const currentActive = modIdx(Math.round(posRef.current), M);
            const nextActive = modIdx(currentActive + 1, M);
            select(nextActive);
        }, 3500);

        return () => clearInterval(interval);
    }, [M, select, isHovered, isInView, isTabVisible]);

    const center = Math.round(posDisplay);
    const renderItems: number[] = [];
    const seen = new Set<number>();
    for (let s = -buffer; s <= buffer; s++) {
        const idx = modIdx(center + s, M);
        if (!seen.has(idx)) {
            seen.add(idx);
            renderItems.push(idx);
        }
    }

    function getVisualSlot(itemIdx: number): number {
        let slot = itemIdx - posDisplay;
        slot = slot % M;
        if (slot > M / 2) slot -= M;
        if (slot < -M / 2) slot += M;
        return slot;
    }

    function slotStyle(slot: number) {
        const angle = slot * dPsi;
        const x = R * Math.sin(angle);
        const y = R * (1 - Math.cos(angle));
        const deg = (angle * 180) / Math.PI;
        const absSlot = Math.abs(slot);
        const depth = Math.max(0, 1 - (0.55 * absSlot) / Math.max(1, half));
        const scale = 0.55 + 0.45 * depth;
        const opacity =
            absSlot <= fadeInner
                ? 1
                : absSlot >= fadeEnd
                  ? 0
                  : 1 - (absSlot - fadeInner) / (fadeEnd - fadeInner);
        const zIndex = Math.round(depth * 100) + (absSlot < 0.5 ? 100 : 0);
        return { x, y, deg, scale, opacity, zIndex };
    }

    const imgSweep = 260,
        imgDip = 150;
    const imageVariants = {
        enter: (d: number) => ({
            x: d * imgSweep,
            y: imgDip,
            opacity: 0,
            scale: 0.82,
            rotate: d * 8,
        }),
        center: { x: 0, y: 0, opacity: 1, scale: 1, rotate: 0 },
        exit: (d: number) => ({
            x: -d * imgSweep,
            y: imgDip,
            opacity: 0,
            scale: 0.82,
            rotate: -d * 8,
        }),
    };

    return (
        <div
            ref={containerRef}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{
                position: "relative",
                width: "100%",
                height: "auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 24,
                overflow: "hidden",
                boxSizing: "border-box",
                background: backgroundColor,
            }}
        >
            <div
                style={{
                    position: "relative",
                    width: imageWidth,
                    height: imageHeight,
                    flex: "0 0 auto",
                    borderRadius: cardRadiusPx,
                    overflow: "hidden",
                    background: currentTheme.bg,
                    border: currentTheme.border,
                    boxShadow: currentTheme.shadow,
                    transition: "background 0.5s ease, border 0.5s ease, box-shadow 0.5s ease",
                }}
            >
                <AnimatePresence mode="popLayout" initial={false} custom={dir}>
                    <motion.div
                        key={active}
                        custom={dir}
                        variants={imageVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        style={{
                            position: "absolute",
                            inset: 0,
                            background: currentTheme.bg,
                        }}
                    >
                        {srcOf(list[active]?.image) && (
                            <img
                                src={srcOf(list[active]?.image)}
                                alt=""
                                draggable={false}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    display: "block",
                                }}
                            />
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>

            {labelShow && (
                <AnimatePresence mode="popLayout" initial={false}>
                    <motion.div
                        key={`label-${active}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        style={{
                            flex: "0 0 auto",
                            width: "90%",
                            maxWidth: "550px",
                            textAlign: "center",
                            color: labelColor,
                            transform: `translate(${labelX}px, ${labelY}px)`,
                            fontFamily: labelFont?.fontFamily,
                            fontWeight: labelFont?.fontWeight as any,
                            fontSize: labelFont?.fontSize,
                            fontStyle: labelFont?.fontStyle,
                            letterSpacing: labelFont?.letterSpacing,
                            lineHeight: labelFont?.lineHeight,
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                            alignItems: "center",
                            background: "rgba(0, 0, 0, 0.55)",
                            backdropFilter: "blur(20px)",
                            WebkitBackdropFilter: "blur(20px)",
                            border: "1px solid rgba(255, 174, 0, 0.12)",
                            borderRadius: "24px",
                            padding: "24px 32px",
                            boxShadow: "0 15px 40px rgba(0, 0, 0, 0.6)",
                            marginTop: "16px",
                        }}
                    >
                        <div style={{ fontWeight: 800, color: "#FFAE00", fontSize: "1.6rem" }}>
                            {list[active]?.label ?? ""}
                        </div>
                        {list[active]?.role && (
                            <div style={{ fontSize: "1.15rem", color: "#e0e0e0", fontWeight: 600 }}>
                                {list[active]?.role}
                            </div>
                        )}
                        {list[active]?.bio && (
                            <div style={{ fontSize: "1.05rem", color: "#e0e0e0", maxWidth: "450px", lineHeight: 1.75, marginTop: 4 }}>
                                {list[active]?.bio}
                            </div>
                        )}
                    </motion.div>
                </AnimatePresence>
            )}

            <div
                style={{
                    position: "relative",
                    width: "100%",
                    height: stripHeight,
                    overflow: "hidden",
                    flex: "0 0 auto",
                }}
            >
                {renderItems.map((itemIdx) => {
                    const slot = getVisualSlot(itemIdx);
                    const { x, y, deg, scale, opacity, zIndex } =
                        slotStyle(slot);
                    const isActive = itemIdx === active;
                    const item = list[itemIdx];

                    return (
                        <div
                            key={itemIdx}
                            style={{
                                position: "absolute",
                                left: "50%",
                                top: baseTop,
                                marginLeft: -buttonSize / 2,
                                marginTop: -buttonSize / 2,
                                width: buttonSize,
                                height: buttonSize,
                                transform: `translate(${x}px, ${y}px) rotate(${deg}deg) scale(${scale})`,
                                transformOrigin: "center",
                                opacity,
                                zIndex,
                                willChange: "transform, opacity",
                            }}
                        >
                            <div
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    borderRadius: buttonRadiusPx,
                                    overflow: "hidden",
                                    position: "relative",
                                    transform: `rotate(${-deg}deg)`,
                                    transformOrigin: "center",
                                    background: isActive
                                        ? "#ffffff"
                                        : "rgba(255, 255, 255, 0.45)",
                                    border: isActive 
                                        ? "2px solid #FFAE00" 
                                        : "1px solid rgba(255, 255, 255, 0.25)",
                                    boxShadow: isActive 
                                        ? "0 0 15px rgba(255, 174, 0, 0.6)" 
                                        : "0 4px 10px rgba(0, 0, 0, 0.3)",
                                    backdropFilter: isActive ? undefined : "blur(4px)",
                                    WebkitBackdropFilter: isActive ? undefined : "blur(4px)",
                                    cursor: "pointer",
                                    WebkitTapHighlightColor: "transparent",
                                    transition: "background 0.3s, border 0.3s, box-shadow 0.3s",
                                }}
                                onClick={() => select(itemIdx)}
                            >
                                {srcOf(item?.buttonImage) && (
                                    <img
                                        src={srcOf(item?.buttonImage)}
                                        alt=""
                                        draggable={false}
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            display: "block",
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
